import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import throat from 'throat';
import { MultipleFileInput, MultipleFileInputProps } from 'components/semantic';
import { toast } from 'components/Toast';
import { useSDKApp } from 'App/Application/CurrentApp';
import { Storage } from 'open-leancloud-storage/storage';
import * as LC from 'open-leancloud-storage';
export type LCFile = Awaited<ReturnType<Storage['upload']>>;

const UPLOAD_CONCURRENCY = 3;

export const FileUploader = ({
  onUploaded,
  icon = false,
  app: providedApp,
  ...props
}: Omit<MultipleFileInputProps, 'onChange'> & {
  onUploaded: (filesLength: number, files: LCFile[]) => void;
  app?: LC.App;
}) => {
  const { t } = useTranslation();
  const [uploadProgress, setUploadProgress] = useState<number>();
  const [sdkApp] = useSDKApp();
  const app = providedApp || sdkApp;
  const uploadFile = async (fileList: FileList, e: React.ChangeEvent<HTMLInputElement>) => {
    // TODO: 升级到 react 17 后移除
    e.persist();
    if (!app) return;
    setUploadProgress(0);
    try {
      const files = Array.from(fileList);
      const fileDonePercent = new WeakMap<File, number>();
      const totalSize = files.reduce((prev, current) => prev + current.size, 0);
      const updateProgress = (percent: number, file: File) => {
        fileDonePercent.set(file, percent);
        const totalDoneSize = files.reduce(
          (prev, current) => prev + (fileDonePercent.get(current) || 0) * current.size,
          0
        );
        const progress = Number((totalDoneSize / totalSize).toFixed(0));
        setUploadProgress(progress);
      };

      const storage = app.storage();
      const uploadTasks = files.map(
        (file) => () =>
          storage
            .upload(file.name, file, {
              onProgress: ({ percent }) => updateProgress(percent!, file),
            })
            .then((res) => {
              toast.success(`${t('action.upload.successfully')}: ${file.name}`);
              return res;
            })
            .catch((error) => toast.error(`${t('action.upload.failed')}: ${file.name}`, error))
      );
      const lcFiles = await Promise.all(
        uploadTasks.map(throat(UPLOAD_CONCURRENCY, (task) => task()))
      );
      onUploaded(files.length, lcFiles as LCFile[]);
    } catch (error) {
      toast.error(t('action.upload.failed'), error);
    }
    setUploadProgress(undefined);
    // reset the file input
    e.target.value = '';
  };
  const loading = uploadProgress !== undefined;
  return (
    <MultipleFileInput
      {...props}
      icon={icon}
      disabled={loading}
      onChange={uploadFile}
      hint={
        props.hint ||
        (uploadProgress === undefined
          ? t('action.upload')
          : `${t('label.uploading')} (${uploadProgress}%)`)
      }
    />
  );
};
